import React, { useEffect, useRef, useState } from 'react'

import { TextProps } from 'components_new/atoms/Text'

import { useTheme } from '@mui/material'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import InputBase from 'components_new/atoms/InputBase'

import OverflowText from 'components_new/molecules/OverflowText'

export interface EditableTitleProps extends Omit<TextProps, 'children'> {
  actions?: boolean
  autoFocus?: boolean
  disabled?: boolean
  editIcon?: boolean
  // eslint-disable-next-line no-unused-vars
  onSubmit: (value: string | null) => void
  placeholder?: string
  value: string | null
  variant:
    | 'body1'
    | 'body2'
    | 'button'
    | 'caption'
    | 'gigantic'
    | 'h1'
    | 'h2'
    | 'h3'
    | 'h4'
    | 'h5'
    | 'h6'
    | 'overline'
    | 'subtitle1'
    | 'subtitle2'
}

/**
 * The EditableTitle component is used let the
 * user edit a single line text element
 * without the need of opening a dialog.
 */

const EditableTitle = React.forwardRef(
  (props: EditableTitleProps, ref: any) => {
    const {
      actions,
      autoFocus,
      disabled,
      editIcon,
      onSubmit,
      placeholder,
      sx,
      value,
      ...rest
    } = props

    const { t } = useTranslation()
    const theme = useTheme()
    const font = theme.typography[props.variant]

    const [editValue, setEditValue] = useState(value)
    const [isActive, setIsActive] = useState(autoFocus && !disabled)

    const inputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
      if (isActive) {
        inputRef.current?.focus()
      }
    }, [isActive])

    const handleSubmit = () => {
      setIsActive(false)
      if (editValue !== value) {
        onSubmit(editValue)
      }
    }

    const handleCancel = () => {
      setIsActive(false)
      setEditValue(value)
    }

    return !isActive ? (
      <OverflowText
        color={!value ? 'text.disabled' : undefined}
        {...rest}
        onClick={(e) => {
          if (!editIcon && !disabled) {
            e.stopPropagation()

            setEditValue(value)
            setIsActive(true)
          }
        }}
        sx={{
          ...(!editIcon && !disabled
            ? {
                cursor: 'text',
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }
            : null),
          ...(editIcon && !disabled
            ? {
                ['&:hover']: {
                  ['& .EditIcon']: {
                    opacity: 1
                  }
                }
              }
            : null),
          ...sx
        }}
      >
        {value || placeholder || t('global', 'unnamed')}
        {editIcon ? (
          <IconButton
            className="EditIcon"
            disablePadding={true}
            onClick={(e) => {
              if (!disabled) {
                e.stopPropagation()

                setEditValue(value)
                setIsActive(true)
              }
            }}
            size="small"
            sx={{ ml: 0.5, opacity: 0 }}
          >
            <Icon fontSize="inherit" name="Edit" />
          </IconButton>
        ) : null}
      </OverflowText>
    ) : (
      <Box sx={{ position: 'relative', ...sx }}>
        <InputBase
          autoFocus={autoFocus}
          fullWidth
          inputRef={inputRef}
          onChange={(event) => setEditValue(event.target.value)}
          onBlur={() => handleSubmit()}
          onKeyUp={(event) => {
            if (event.key === 'Enter') {
              handleSubmit()
            }
          }}
          placeholder={placeholder}
          ref={ref}
          sx={{
            ...font,
            ...(props.fontSize ? { fontSize: props.fontSize } : null),
            bgcolor: 'action.focus',
            '& .MuiInputBase-input': {
              p: 0,
              height: 'auto'
            },
            width: '100%'
          }}
          value={editValue}
        />
        {actions ? (
          <Box
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              display: 'flex',
              gap: 0.25,
              position: 'absolute',
              p: 0.25,
              right: 0,
              top: 'calc(100% + 2px)',
              zIndex: 9999
            }}
          >
            <IconButton color="default" onClick={handleCancel} size="small">
              <Icon fontSize="small" name="Close" />
            </IconButton>
            <IconButton color="primary" onClick={handleSubmit} size="small">
              <Icon fontSize="small" name="Check" />
            </IconButton>
          </Box>
        ) : null}
      </Box>
    )
  }
)

export default EditableTitle
